import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getProducts, deleteProduct, bulkDeleteProducts, bulkUpdateCategory, getCategories, createProduct, uploadProductImage, adjustStock, getWarehouses } from '../services/api';
import { Plus, Search, Trash2, Package, ChevronUp, ChevronDown, Download, FolderEdit, Camera, Zap, Pencil, Check, X, ChevronRight, Filter } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import Pagination from '../components/Pagination';
import { useCachedData, invalidateCache } from '../hooks/useDataCache';
import { TableSkeleton } from '../components/Skeletons';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function Products() {
    const navigate = useNavigate();
    const quickFileRef = useRef(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchProducts = useCallback(() => getProducts(), []);
    const fetchCategories = useCallback(() => getCategories(), []);
    const { data: cachedProducts, invalidate: refreshProducts } = useCachedData('products', fetchProducts);
    const { data: cachedCategories } = useCachedData('categories', fetchCategories);

    useEffect(() => {
        if (cachedProducts) { setProducts(cachedProducts); setLoading(false); }
    }, [cachedProducts]);
    useEffect(() => { if (cachedCategories) setCategories(cachedCategories); }, [cachedCategories]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [sortField, setSortField] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 20;
    const [showCatModal, setShowCatModal] = useState(false);
    const [bulkCat, setBulkCat] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [quickName, setQuickName] = useState('');
    const [quickFile, setQuickFile] = useState(null);
    const [quickPreview, setQuickPreview] = useState(null);
    const [quickSaving, setQuickSaving] = useState(false);
    const [editingStock, setEditingStock] = useState(null); // product id being edited
    const [stockValues, setStockValues] = useState({}); // { productId: newValue, variantId: newValue }
    const [expandedProduct, setExpandedProduct] = useState(null); // product id with variants expanded
    const [defaultWarehouse, setDefaultWarehouse] = useState(null);
    const [savingStock, setSavingStock] = useState(false);

    useEffect(() => {
        getWarehouses().then(r => {
            const whs = r.data;
            const def = whs.find(w => w.is_default) || whs[0];
            if (def) setDefaultWarehouse(def.id);
        }).catch(() => {});
    }, []);

    const fetchData = async () => {
        invalidateCache('products');
        refreshProducts();
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const uniqueCategories = useMemo(() => [...new Set(products.map(p => p.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr')), [products]);

    const filtered = useMemo(() => {
        setPage(1);
        let list = products.filter(p => {
            const matchSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.barcode?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchCat = !filterCategory || p.category === filterCategory;
            return matchSearch && matchCat;
        });
        if (sortField) {
            list = [...list].sort((a, b) => {
                let va = a[sortField], vb = b[sortField];
                if (va == null) va = '';
                if (vb == null) vb = '';
                if (typeof va === 'number' && typeof vb === 'number') {
                    return sortDir === 'asc' ? va - vb : vb - va;
                }
                return sortDir === 'asc'
                    ? String(va).localeCompare(String(vb), 'tr')
                    : String(vb).localeCompare(String(va), 'tr');
            });
        }
        return list;
    }, [products, searchQuery, filterCategory, sortField, sortDir]);

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === filtered.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(filtered.map(p => p.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`${selected.size} urun silinecek. Devam?`)) return;
        try {
            await bulkDeleteProducts([...selected]);
            toast.success(`${selected.size} urun silindi`);
            setSelected(new Set());
            fetchData();
        } catch { toast.error('Silinemedi'); }
    };

    const handleBulkCategory = async () => {
        if (!bulkCat.trim()) return;
        try {
            await bulkUpdateCategory([...selected], bulkCat);
            toast.success(`${selected.size} urun guncellendi`);
            setSelected(new Set());
            setShowCatModal(false);
            setBulkCat('');
            fetchData();
        } catch { toast.error('Guncellenemedi'); }
    };

    const handleExcelExport = () => {
        const data = filtered.map(p => ({
            'Referans': p.reference || '',
            'Urun': p.name,
            'Barkod': p.barcode || '',
            'Kategori': p.category || '',
            'Agirlik': p.weight ? `${p.weight} ${p.weight_unit || 'kg'}` : '',
            'Alis Fiyati': p.purchase_price ?? '',
            'Fiyat A': p.price_a ?? '',
            'Stok': p.current_stock,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Urunler');
        XLSX.writeFile(wb, 'urunler.xlsx');
        toast.success('Excel indirildi');
    };

    const handleQuickAdd = async () => {
        if (!quickName.trim()) return toast.error('Urun adi gerekli');
        setQuickSaving(true);
        try {
            const res = await createProduct({ name: quickName.trim() });
            const newId = res.data?.id;
            if (quickFile && newId) {
                await uploadProductImage(newId, quickFile);
            }
            toast.success('Urun eklendi');
            setShowQuickAdd(false);
            setQuickName('');
            setQuickFile(null);
            setQuickPreview(null);
            fetchData();
        } catch { toast.error('Eklenemedi'); }
        finally { setQuickSaving(false); }
    };

    const handleQuickFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setQuickFile(file);
        setQuickPreview(URL.createObjectURL(file));
    };

    const startStockEdit = (product, e) => {
        e.stopPropagation();
        const hasVariants = product.variants?.length > 0;
        if (hasVariants) {
            setExpandedProduct(expandedProduct === product.id ? null : product.id);
            const vals = {};
            product.variants.forEach(v => { vals[v.id] = v.current_stock || 0; });
            setStockValues(vals);
            setEditingStock(product.id);
        } else {
            setEditingStock(product.id);
            setStockValues({ [product.id]: product.current_stock || 0 });
            setExpandedProduct(null);
        }
    };

    const cancelStockEdit = (e) => {
        if (e) e.stopPropagation();
        setEditingStock(null);
        setStockValues({});
        setExpandedProduct(null);
    };

    const saveStockEdit = async (product, e) => {
        if (e) e.stopPropagation();
        if (!defaultWarehouse) { toast.error('Depo bulunamadi'); return; }
        setSavingStock(true);
        try {
            const hasVariants = product.variants?.length > 0;
            if (hasVariants) {
                for (const v of product.variants) {
                    const newVal = parseInt(stockValues[v.id]) || 0;
                    if (newVal !== (v.current_stock || 0)) {
                        await adjustStock({ product_id: product.id, warehouse_id: defaultWarehouse, quantity: newVal, variant_id: v.id });
                    }
                }
            } else {
                const newVal = parseInt(stockValues[product.id]) || 0;
                if (newVal !== (product.current_stock || 0)) {
                    await adjustStock({ product_id: product.id, warehouse_id: defaultWarehouse, quantity: newVal });
                }
            }
            toast.success('Stok guncellendi');
            cancelStockEdit();
            fetchData();
        } catch { toast.error('Stok guncellenemedi'); }
        finally { setSavingStock(false); }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ChevronUp size={12} className="opacity-0 group-hover:opacity-30" />;
        return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
    };

    const ThCell = ({ field, children, align }) => (
        <th
            className={`cursor-pointer select-none group ${align === 'right' ? 'text-right' : ''}`}
            onClick={() => handleSort(field)}
        >
            <span className="inline-flex items-center gap-1">
                {children} <SortIcon field={field} />
            </span>
        </th>
    );

    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="products-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h1 className="page-title">Urunler</h1>
                        <p className="page-subtitle">{products.length} urun</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleExcelExport} className="btn btn-secondary text-sm hidden sm:inline-flex" data-testid="excel-export-btn">
                            <Download size={15} /> Excel
                        </button>
                        <button onClick={() => { setShowQuickAdd(true); setQuickName(''); setQuickFile(null); setQuickPreview(null); }} className="btn btn-secondary text-sm" data-testid="quick-add-btn">
                            <Zap size={15} /> <span className="hidden sm:inline">Hizli Ekle</span><span className="sm:hidden">Hizli</span>
                        </button>
                        <button onClick={() => navigate('/products/new')} className="btn btn-primary text-sm" data-testid="add-product-btn">
                            <Plus size={15} /> <span className="hidden sm:inline">Urun Ekle</span>
                        </button>
                    </div>
                </div>

                {/* Search + Category Filter + Bulk Actions */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[180px] max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Urun ara..."
                            className="form-input pl-10"
                            data-testid="search-input"
                        />
                    </div>
                    {uniqueCategories.length > 0 && (
                        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1); }}
                            className="input-apple text-sm h-[42px] w-auto min-w-[140px]" data-testid="filter-category-select">
                            <option value="">Tum Kategoriler</option>
                            {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    )}
                    {(filterCategory) && (
                        <button onClick={() => { setFilterCategory(''); setPage(1); }} className="text-xs text-[#6B7280] hover:text-[#EF4444] transition-colors">Temizle</button>
                    )}
                    {selected.size > 0 && (
                        <div className="flex items-center gap-2 animate-in fade-in" data-testid="bulk-actions">
                            <span className="text-sm text-gray-500">{selected.size} secili</span>
                            <button onClick={handleBulkDelete} className="btn btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-delete-btn">
                                <Trash2 size={14} /> Sil
                            </button>
                            <button onClick={() => setShowCatModal(true)} className="btn btn-secondary text-sm" data-testid="bulk-category-btn">
                                <FolderEdit size={14} /> Kategori
                            </button>
                        </div>
                    )}
                </div>

                {/* Table */}
                <div className="data-card">
                    {loading ? (
                        <div className="p-8 space-y-3">
                            {[1,2,3,4,5].map(i => <div key={i} className="skeleton h-10 rounded" />)}
                        </div>
                    ) : filtered.length > 0 ? (
                        <>
                        <div className="table-container">
                            <table className="table mobile-cards" data-testid="products-table" style={{ tableLayout: 'fixed' }}>
                                <colgroup>
                                    <col style={{ width: '40px' }} />
                                    <col style={{ width: '40px' }} />
                                    <col style={{ width: '100px' }} />
                                    <col />
                                    <col style={{ width: '110px' }} />
                                    <col style={{ width: '100px' }} />
                                    <col style={{ width: '80px' }} />
                                    <col style={{ width: '90px' }} />
                                    <col style={{ width: '80px' }} />
                                    <col style={{ width: '70px' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className="w-10">
                                            <input
                                                type="checkbox"
                                                checked={selected.size === filtered.length && filtered.length > 0}
                                                onChange={toggleAll}
                                                className="rounded border-gray-300"
                                                data-testid="select-all-checkbox"
                                            />
                                        </th>
                                        <th className="w-10"></th>
                                        <ThCell field="reference">Referans</ThCell>
                                        <ThCell field="name">Urun</ThCell>
                                        <ThCell field="barcode">Barkod</ThCell>
                                        <ThCell field="category">Kategori</ThCell>
                                        <ThCell field="weight" align="right">Agirlik</ThCell>
                                        <ThCell field="purchase_price" align="right">Alis Fiyati</ThCell>
                                        <ThCell field="price_a" align="right">Fiyat A</ThCell>
                                        <th className="text-right">Stok</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((p) => (
                                        <React.Fragment key={p.id}>
                                        <tr
                                            data-testid={`product-row-${p.id}`}
                                            className={`cursor-pointer group ${selected.has(p.id) ? 'bg-blue-50' : ''}`}
                                            onClick={() => { if (editingStock !== p.id) navigate(`/products/${p.id}`); }}
                                        >
                                            <td onClick={e => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(p.id)}
                                                    onChange={() => toggleSelect(p.id)}
                                                    className="rounded border-gray-300"
                                                    data-testid={`select-${p.id}`}
                                                />
                                            </td>
                                            <td>
                                                <div className="w-8 h-8 rounded-lg bg-gray-50 overflow-hidden flex items-center justify-center">
                                                    {p.image_url ? (
                                                        <img src={`${API_BASE}${p.image_url}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-300"><svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"/></svg></div>'; }} />
                                                    ) : (
                                                        <Package size={14} className="text-gray-300" />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="mobile-hide text-gray-500 font-mono text-xs">{p.reference || '—'}</td>
                                            <td className="font-medium text-gray-900">
                                                {p.name}
                                                {p.variants?.length > 0 && <span className="text-xs text-blue-500 ml-1">({p.variants.length} varyant)</span>}
                                            </td>
                                            <td className="mobile-hide text-gray-500 font-mono text-xs">{p.barcode || '—'}</td>
                                            <td className="mobile-hide text-gray-500">{p.category || '—'}</td>
                                            <td className="mobile-hide text-right text-gray-500">
                                                {p.weight ? `${p.weight} ${p.weight_unit || 'kg'}` : '—'}
                                            </td>
                                            <td className="mobile-hide text-right font-medium">
                                                {p.purchase_price != null ? p.purchase_price.toFixed(2) : '—'}
                                            </td>
                                            <td className="mobile-hide text-right font-medium">
                                                {p.price_a != null ? p.price_a.toFixed(2) : '—'}
                                            </td>
                                            <td className="text-right" onClick={e => e.stopPropagation()}>
                                                {editingStock === p.id && !(p.variants?.length > 0) ? (
                                                    <div className="flex items-center justify-end gap-1">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={stockValues[p.id] ?? ''}
                                                            onChange={e => setStockValues(prev => ({ ...prev, [p.id]: e.target.value }))}
                                                            className="w-16 h-7 text-right text-sm border border-blue-300 rounded px-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                            autoFocus
                                                            data-testid={`stock-input-${p.id}`}
                                                            onKeyDown={e => { if (e.key === 'Enter') saveStockEdit(p, e); if (e.key === 'Escape') cancelStockEdit(e); }}
                                                        />
                                                        <button onClick={(e) => saveStockEdit(p, e)} disabled={savingStock} className="w-6 h-6 rounded bg-green-50 hover:bg-green-100 flex items-center justify-center" data-testid={`stock-save-${p.id}`}><Check size={12} className="text-green-600" /></button>
                                                        <button onClick={cancelStockEdit} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 flex items-center justify-center" data-testid={`stock-cancel-${p.id}`}><X size={12} className="text-gray-500" /></button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        {p.variants?.length > 0 && editingStock === p.id && (
                                                            <>
                                                                <button onClick={(e) => saveStockEdit(p, e)} disabled={savingStock} className="w-6 h-6 rounded bg-green-50 hover:bg-green-100 flex items-center justify-center" data-testid={`stock-save-${p.id}`}><Check size={12} className="text-green-600" /></button>
                                                                <button onClick={cancelStockEdit} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 flex items-center justify-center" data-testid={`stock-cancel-${p.id}`}><X size={12} className="text-gray-500" /></button>
                                                            </>
                                                        )}
                                                        <span className={`font-medium ${p.current_stock < p.low_stock_threshold ? 'text-red-500' : 'text-gray-900'}`}>
                                                            {p.current_stock}
                                                        </span>
                                                        <button onClick={(e) => startStockEdit(p, e)} className="w-6 h-6 rounded hover:bg-gray-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`stock-edit-${p.id}`}>
                                                            <Pencil size={11} className="text-gray-400" />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                        {/* Variant rows for stock editing */}
                                        {expandedProduct === p.id && p.variants?.length > 0 && p.variants.map(v => (
                                            <tr key={v.id} className="bg-blue-50/40 border-l-2 border-l-blue-200" data-testid={`variant-row-${v.id}`}>
                                                <td></td>
                                                <td></td>
                                                <td className="mobile-hide text-gray-400 font-mono text-xs">{v.sku || '—'}</td>
                                                <td className="text-gray-600 text-sm pl-6">
                                                    <div className="flex items-center gap-1">
                                                        <ChevronRight size={12} className="text-blue-400" />
                                                        {v.name}
                                                    </div>
                                                </td>
                                                <td className="mobile-hide text-gray-400 font-mono text-xs">{v.barcode || '—'}</td>
                                                <td colSpan={3}></td>
                                                <td></td>
                                                <td className="text-right" onClick={e => e.stopPropagation()}>
                                                    {editingStock === p.id ? (
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={stockValues[v.id] ?? ''}
                                                            onChange={e => setStockValues(prev => ({ ...prev, [v.id]: e.target.value }))}
                                                            className="w-16 h-7 text-right text-sm border border-blue-300 rounded px-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                            data-testid={`stock-input-${v.id}`}
                                                            onKeyDown={e => { if (e.key === 'Enter') saveStockEdit(p, e); if (e.key === 'Escape') cancelStockEdit(e); }}
                                                        />
                                                    ) : (
                                                        <span className="text-sm text-gray-600">{v.current_stock || 0}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination currentPage={page} totalPages={Math.ceil(filtered.length / PAGE_SIZE)} onPageChange={setPage} />
                        </>
                    ) : (
                        <div className="text-center py-16">
                            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-base font-medium text-gray-900 mb-1">Urun yok</h3>
                            <p className="text-sm text-gray-500 mb-4">Ilk urununu ekle</p>
                            <button onClick={() => navigate('/products/new')} className="btn btn-primary">
                                <Plus size={15} /> Urun Ekle
                            </button>
                        </div>
                    )}
                </div>

                {/* Bulk Category Modal */}
                <Dialog open={showCatModal} onOpenChange={setShowCatModal}>
                    <DialogContent className="sm:max-w-sm">
                        <DialogHeader>
                            <DialogTitle>Kategori Degistir</DialogTitle>
                            <DialogDescription>{selected.size} urun guncellenecek</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <select value={bulkCat} onChange={e => setBulkCat(e.target.value)} className="form-select" data-testid="bulk-category-select">
                                <option value="">Kategori secin...</option>
                                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                            </select>
                            <div className="flex gap-3">
                                <button onClick={() => setShowCatModal(false)} className="btn btn-secondary flex-1">Iptal</button>
                                <button onClick={handleBulkCategory} className="btn btn-primary flex-1" data-testid="apply-category-btn">Uygula</button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Quick Add Modal */}
                <Dialog open={showQuickAdd} onOpenChange={setShowQuickAdd}>
                    <DialogContent className="sm:max-w-sm" aria-label="Hizli Urun Ekle">
                        <DialogHeader>
                            <DialogTitle>Hizli Urun Ekle</DialogTitle>
                            <DialogDescription>Foto cek, isim gir. Detaylar sonra duzenlenir.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-3">
                            <input type="file" ref={quickFileRef} accept="image/*" capture="environment" className="hidden" onChange={handleQuickFile} data-testid="quick-file-input" />
                            <div
                                onClick={() => quickFileRef.current?.click()}
                                className="w-full aspect-square max-h-52 rounded-2xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors overflow-hidden"
                                data-testid="quick-photo-area"
                            >
                                {quickPreview ? (
                                    <img src={quickPreview} alt="Preview" className="w-full h-full object-cover" />
                                ) : (
                                    <>
                                        <Camera size={32} className="text-gray-300 mb-2" />
                                        <span className="text-sm text-gray-400">Foto Cek / Sec</span>
                                    </>
                                )}
                            </div>
                            <div>
                                <input
                                    type="text"
                                    value={quickName}
                                    onChange={e => setQuickName(e.target.value)}
                                    placeholder="Urun adi *"
                                    className="form-input text-base"
                                    autoFocus
                                    data-testid="quick-name-input"
                                    onKeyDown={e => { if (e.key === 'Enter') handleQuickAdd(); }}
                                />
                            </div>
                            <div className="flex gap-3">
                                <button type="button" onClick={() => setShowQuickAdd(false)} className="btn btn-secondary flex-1">Iptal</button>
                                <button type="button" onClick={handleQuickAdd} disabled={quickSaving} className="btn btn-primary flex-1" data-testid="quick-save-btn">
                                    {quickSaving ? 'Kaydediliyor...' : 'Kaydet'}
                                </button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
